// Shared formatting for The Curve — settle-date labels, dates, values.

import { useEffect, useState } from 'react';

// NFL season anchors — graduates to server config when the season rolls.
export const SEASON = { week1Tuesday: '2026-09-08', weeks: 18 };

/** "Week 6" when the date lands in the season, otherwise "Aug 28" / landmarks. */
export function resolveLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00Z`);
  const w1 = new Date(`${SEASON.week1Tuesday}T12:00:00Z`);
  const week = Math.floor((d - w1) / (7 * 86400000)) + 1;
  if (week >= 1 && week <= SEASON.weeks) return `Week ${week}`;
  if (week > SEASON.weeks) return 'after the draft';
  if (week === 0) return 'kickoff';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtDate(dateStr) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
}

export function fmtTick(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .toUpperCase();
}

export function addDaysStr(dateStr, n) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const fmtV = (v) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v));

/** One line-row's display state — shared by the rail and the history list.
    kind: today (locked, ungraded) · track (green, live) · fit (dark, settled) */
export function drawRowState(draw, todayLineId) {
  const line = draw.line;
  if (line.status === 'settled') {
    return {
      meta: `SETTLED ${fmtTick(line.resolveDate)}`,
      chip: { kind: 'fit', label: draw.settledScore != null ? String(Math.round(draw.settledScore)) : '—' },
    };
  }
  if (line.id === todayLineId) {
    return { meta: 'DRAWN TODAY', chip: { kind: 'today', label: 'LOCKED' } };
  }
  if (draw.trackingScore == null) {
    return { meta: 'FIRST GRADE TONIGHT', chip: null };
  }
  const label = resolveLabel(line.resolveDate);
  return {
    meta: `SETTLES ${(label.startsWith('Week') ? label : fmtTick(line.resolveDate)).toUpperCase()}`,
    chip: { kind: 'track', label: String(Math.round(draw.trackingScore)) },
  };
}

// ── next-morning ceremony ── "was I right?" is the core loop, but grading
// happens silently overnight. This surfaces the most recently settled draw
// once, using only fields the API already returns — no invented data.

const LAST_SEEN_SETTLED_KEY = 'ft_curve_last_seen_settled';

function getLastSeenSettledId() {
  try {
    return localStorage.getItem(LAST_SEEN_SETTLED_KEY);
  } catch (e) {
    return null;
  }
}

function markSettledSeen(id) {
  try {
    localStorage.setItem(LAST_SEEN_SETTLED_KEY, id);
  } catch (e) {
    /* storage blocked — worst case the banner repeats once */
  }
}

/** The most recently settled draw in a tape, by the line's resolve date. */
export function latestSettledDraw(tape) {
  const draws = tape && Array.isArray(tape.draws) ? tape.draws : [];
  const settled = draws.filter((d) => d.line && d.line.status === 'settled');
  if (!settled.length) return null;
  return settled.reduce((latest, d) => {
    if (!latest) return d;
    return (d.line.resolveDate || '') > (latest.line.resolveDate || '') ? d : latest;
  }, null);
}

/** Kicker + sentence for the ceremony banner — real fields only:
    player name, your final call, what reality settled at, and the
    percentile of the room's calls you beat. Null if the shape is thin. */
export function ceremonyCopy(draw) {
  if (!draw || !draw.line) return null;
  const line = draw.line;
  const name = line.player && line.player.name;
  const points = Array.isArray(draw.points) ? draw.points : [];
  const yourCall = points.length ? points[points.length - 1] : null;
  const marks = Array.isArray(line.marks) ? line.marks : [];
  const reality = marks.length ? marks[marks.length - 1].value : null;
  if (!name || yourCall == null || reality == null) return null;

  const pct = draw.percentile != null ? Math.round(draw.percentile) : null;
  const sentence =
    pct != null
      ? `${name} settled at ${fmtV(reality)}. You called ${fmtV(yourCall)} — closer than ${pct}% of the room's calls.`
      : `${name} settled at ${fmtV(reality)}. You called ${fmtV(yourCall)}.`;

  return { kicker: "Yesterday's call", sentence };
}

/** Once-per-settle ceremony state, shared by CurveToday and CurveDeskPanel.
    Finds the newest settled draw in `tape`; if it's newer than the last one
    acknowledged (tracked in localStorage), returns its copy once. */
export function useCurveCeremony(tape) {
  const [copy, setCopy] = useState(null);

  useEffect(() => {
    const latest = latestSettledDraw(tape);
    if (!latest) return;
    if (getLastSeenSettledId() === latest.id) return;
    const next = ceremonyCopy(latest);
    markSettledSeen(latest.id);
    if (next) setCopy(next);
  }, [tape]);

  return copy;
}
